import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import JourneyV2Details from 'components/Flights/V2/FareFamily/JourneyV2Details'
import FlightCartReviewTileExtension from 'components/Pages/CartReviewPage/FlightCartReviewTileExtension'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import { useRightPanelTripDrawerPresentation } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import TextButton from 'LuxKit/Button/TextButton'
import { CurrencyProvider } from 'contexts/currencyContext'
import type { TransportOption } from 'tripPlanner/api/types'
import type { BookFlightResult } from 'tripPlanner/components/Chat/Flights/flightRecommendationBookingTypes'
import { buildFlightName } from '../../utils/transportNames'
import type { ShortlistedFlightDetailsSection } from './types'
import { drawerBodyInlinePaddingStyles } from '../drawerBodyLayout.styles'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'

const DrawerContent = styled(Group)`
  width: 100%;
  margin-block-start: ${rem(24)};
  margin-block-end: ${rem(24)};
  margin-inline: auto;
  padding-block-start: 0;
  padding-block-end: 0;
  ${drawerBodyInlinePaddingStyles}
  box-sizing: border-box;
`

interface Props {
  isOpen: boolean
  option: TransportOption
  sections: Array<ShortlistedFlightDetailsSection>
  price?: number
  currencyCode?: string
  regularPrice?: number
  isLuxPlusFare?: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>
}

interface SectionProps {
  section: ShortlistedFlightDetailsSection
}

function Section({ section }: SectionProps) {
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>(
    undefined,
  )
  const [fareRulesParams, setFareRulesParams] = useState<any>(undefined)

  if ('cartFlight' in section) {
    return (
      <FlightCartReviewTileExtension
        flight={section.cartFlight}
        isClassicReward={section.isClassicReward}
      />
    )
  }

  return (
    <JourneyV2Details
      journey={section.journey}
      fareRulesURL={fareRulesURL}
      fareRulesParams={fareRulesParams}
      setFareRulesURL={setFareRulesURL}
      setFareRulesParams={setFareRulesParams}
    />
  )
}

function ShortlistedFlightDetailsDrawer({
  isOpen,
  option,
  sections,
  price,
  currencyCode,
  regularPrice,
  isLuxPlusFare,
  onClose,
  onAfterClose,
  onBookNow,
}: Props) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const handleBookNow = () => {
    onClose()
    onBookNow?.(option)
  }

  const economyFromLabel = intl.formatMessage({
    id: 'qqmYB_u5YxspNNLr2yWr',
    defaultMessage: 'Economy from',
    description:
      'Price caption shown above non-booked shortlisted flight price in the flight details drawer',
  })

  const bookNowLabel = intl.formatMessage({
    id: '4VTNHzs2wLQP5XngJurI',
    defaultMessage: 'Book now',
    description:
      'Button label to book a shortlisted flight in the trip planner itinerary',
  })

  const drawerTitle = buildFlightName(
    intl,
    option,
    intl.formatMessage({
      id: 'b9dYE4Xzwk-zhm5-Twgd',
      defaultMessage: 'Flight details',
      description:
        'Fallback title for a flight details drawer in the trip planner',
    }),
  )
  const content = useMemo(() => {
    if (sections.length <= 1) {
      return sections.map((section) => (
        <Section key={section.key} section={section} />
      ))
    }

    return sections.flatMap((section, index) => {
      const node = <Section key={section.key} section={section} />
      if (index === 0) {
        return [node, <Divider key={`${section.key}-divider`} kind="primary" />]
      }
      return [node]
    })
  }, [sections])

  const showPrice = price != null && !!currencyCode
  const showPricingRow = showPrice || !!onBookNow
  const showLuxPlusPrice = isLuxPlusFare === true && Number.isFinite(regularPrice)

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={<TripDrawerHeaderTitle title={drawerTitle} />}
      footer={(
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={drawerTitle}
          isOpen={isOpen}
        />
      )}
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={drawerTitle}
      />
      <DrawerContent direction="vertical" gap={24}>
        {showPricingRow && (
          <Group
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="end"
          >
            <Group direction="vertical" gap={4}>
              {showPrice && (
                <CurrencyProvider currency={currencyCode}>
                  <PriceRowPriceCaption>
                    {economyFromLabel}
                  </PriceRowPriceCaption>
                  {showLuxPlusPrice ? (
                    <PriceRowLuxPlusPrice size="L" memberPrice={price} nonMemberPrice={regularPrice} saleUnit="person" />
                  ) : (
                    <PriceRowPrice size="L" price={price} saleUnit="person" />
                  )}
                </CurrencyProvider>
              )}
            </Group>
            {onBookNow && (
              <TextButton kind="primary" size="large" onClick={handleBookNow}>
                {bookNowLabel}
              </TextButton>
            )}
          </Group>
        )}
        {content}
      </DrawerContent>
    </TripDrawer>
  )
}

export default ShortlistedFlightDetailsDrawer
